/**
 * „Zobrazení" per uživatel: easy („Kde to leží", výchozí)
 * | expert („Alokace") | hybrid („Kde to leží (alokace)").
 *
 * Dva účinky, obojí čistě zobrazovací (KAN-796):
 *
 * 1. vybírá i18n VARIANTU termínu (osm slov, viz ``docs/VOCABULARY.md``),
 * 2. rozhoduje výchozí stav sekcí ``Pokročilé``
 *    (:func:`useAdvancedSectionsExpanded`).
 *
 * ⚠️ Do KAN-796 tady stálo „nikdy nevětví logiku" a přepínač se jmenoval
 * **Slovník**. Bod 2 je z toho výjimka a je JEDINÁ — větví se výhradně
 * ZOBRAZENÍ sekce; data ani API se pořád nemění a hodnota se nikdy
 * nestane nedostupnou (hlavička sekce zůstává vždy v DOM).
 *
 * Persistuje se na profilu (vzor ``ui_theme``), takže jde s uživatelem
 * napříč zařízeními.
 *
 * Obsah slovníku, pravidlo zařazení a rozsah publika: ``docs/VOCABULARY.md``.
 *
 * 🪤 Sloupec se pořád jmenuje ``users.ui_stock_vocabulary``, i když
 * slovník už není skladový. Přejmenování je rozhodnuté jako **NE**
 * (VOCABULARY.md §3): sloupec je interní, uživatel ho nevidí, a migrace
 * má cenu deploy pravidla „starý kód musí tolerovat nové schéma".
 */

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAuthMe, useUpdateProfile } from "@/api/queries/auth";

export type VocabularyMode = "easy" | "expert" | "hybrid";

export const VOCABULARY_MODES: VocabularyMode[] = ["easy", "expert", "hybrid"];

/** i18n namespace, ve kterém slovník bydlí — admin i dílna ho sdílejí. */
export const VOCABULARY_NS = "admin";

/** Hnízdo termínů uvnitř namespace. Jediné místo, kde je cesta natvrdo. */
const TERM_ROOT = "vocabulary.terms.";

/**
 * Když chybí varianta, spadne se sem. Oborový název je vždy
 * srozumitelnější než syrový klíč vysypaný do UI.
 */
const FALLBACK_MODE: VocabularyMode = "expert";

/** Jazyk, ze kterého se bere SEZNAM termínů, když aktuální bundle chybí. */
const DEFAULT_TERMS_LANG = "cs";

function normalize(value: unknown): VocabularyMode {
  return value === "expert" || value === "hybrid" ? value : "easy";
}

/**
 * Jen ČTE zvolený režim — **nikdy sám nefetchuje** (``enabled: false``).
 *
 * 🪤 Musí to tak být. Termín se vykresluje na obrazovkách napříč
 * administrací i dílnou; kdyby si každá z nich zapnula vlastní
 * ``/auth/me``, přibyl by request tam, kde předtím žádný nebyl —
 * a v testech, které kontrolují pořadí volání, by se to projevilo jako
 * rozbitá aserce (což se při zavádění taky stalo: 23 pádů v pěti
 * souborech). Shell si ``/auth/me`` načte tak jako tak; tenhle hook
 * čte z jeho cache. Bez cache spadne na výchozí ``easy``, což je
 * pro zobrazovací preferenci správné chování.
 */
function useVocabularyMode(): VocabularyMode {
  const me = useAuthMe({ enabled: false });
  return normalize(me.data?.ui_stock_vocabulary);
}

/**
 * Má se pokročilá sekce vykreslit rozbalená? (KAN-796)
 *
 * „Zobrazení: Jednoduše" nově neznamená jen lidská slova, ale i
 * **sbalené** sekce ``Pokročilé`` — jeden přepínač, jedna páka na
 * složitost. ``expert`` i ``hybrid`` rozbalují: obojí říká „chci vidět
 * oborový tvar", takže na složitost obrazovky mají týž nárok.
 *
 * 🪤 Tohle je JEDINÉ místo, kde slovník sahá na něco jiného než na
 * volbu i18n varianty. Větvit se smí **zobrazení sekce** — nikdy data,
 * nikdy API, a nikdy ne tak, že by se hodnota stala nedostupnou
 * (``docs/VOCABULARY.md`` §9).
 *
 * Čte přes ``useVocabularyMode``, takže ani tady **nefetchuje** —
 * platí celý komentář u něj.
 */
export function useAdvancedSectionsExpanded(): boolean {
  return useVocabularyMode() !== "easy";
}

/**
 * Plný přístup pro přepínač v menu účtu — čte i zapisuje, a tedy si
 * ``/auth/me`` načíst smí. Menu ho renderuje jen tam, kde ho stejně
 * potřebuje na identitu uživatele.
 */
export function useVocabulary(): {
  mode: VocabularyMode;
  setMode: (mode: VocabularyMode) => void;
  saving: boolean;
} {
  const me = useAuthMe();
  const update = useUpdateProfile();
  const mode = normalize(me.data?.ui_stock_vocabulary);
  return {
    mode,
    setMode: (next) => {
      if (next !== mode) update.mutate({ ui_stock_vocabulary: next });
    },
    saving: update.isPending,
  };
}

/**
 * i18n klíč vybrané varianty termínu.
 *
 * Bere **plnou cestu** termínu — žádný prefix natvrdo. Přidat termín
 * mimo sklad tedy neznamená sáhnout sem ani do ``storageLocations``.
 */
export function vocabKey(term: string, mode: VocabularyMode): string {
  return `${term}.${mode}`;
}

/**
 * Vrátí funkci, která přeloží termín ve zvoleném režimu.
 *
 * ``fallback`` se použije, teprve když chybí i ``expert`` varianta —
 * je to poslední pojistka, ne běžná cesta. Parity gate
 * (``tests/i18n/parity.test.ts``) stejně nepustí termín, který nemá
 * kompletní sadu ve všech jazycích.
 */
export function useVocabularyTerm(): (term: string, fallback: string) => string {
  const { t } = useTranslation(VOCABULARY_NS);
  const mode = useVocabularyMode();
  return useCallback(
    (term: string, fallback: string): string => {
      const path = `${TERM_ROOT}${term}`;
      const chosen = t(vocabKey(path, mode), { defaultValue: "" });
      if (chosen) return chosen;
      const expert =
        mode === FALLBACK_MODE
          ? ""
          : t(vocabKey(path, FALLBACK_MODE), { defaultValue: "" });
      return expert || fallback;
    },
    [t, mode],
  );
}

/**
 * Celý slovník vyřešený pro zvolený režim — pro handshake aplikace
 * (KAN-1361).
 *
 * Bundle aplikace běží v sandboxu bez přístupu k i18n hosta. Aby mluvil
 * týmiž slovy jako shell, dostane v handshake ``{mode, terms}``: každý
 * termín z ``vocabulary.terms`` přeložený stejnou cestou jako
 * :func:`useVocabularyTerm` (zvolená varianta, pak oborová). Bez toho by
 * appka buď psala oborový termín natvrdo, nebo si slovník opsala — obojí
 * je druhá kopie, která se rozejde.
 *
 * Obecné pro každou aplikaci, žádný termín se tu nejmenuje.
 */
export function useVocabularySnapshot(): {
  mode: VocabularyMode;
  terms: Record<string, string>;
} {
  const { i18n } = useTranslation(VOCABULARY_NS);
  const mode = useVocabularyMode();
  const term = useVocabularyTerm();
  // ``term`` je memoizovaný na ``[t, mode]`` a ``t`` se mění se změnou
  // jazyka — snímek se tedy přepočítá právě tehdy, když se může lišit.
  return useMemo(() => {
    // 🪤 Seznam termínů se čte přes ``getResource``, NE přes
    // ``t(…, { returnObjects: true })``. Typovaný ``t`` nad celým
    // ``admin`` bundlem nechal ``tsc --noEmit`` přetéct 2 GB haldy
    // (KAN-1361, build na CI spadl OOM). Klíče pod ``vocabulary.terms``
    // jsou ve všech jazycích stejné (hlídá i18n-parity), takže stačí
    // bundle výchozího jazyka, když aktuální chybí.
    //
    // Bez inicializované i18next instance (izolované testy komponent)
    // vrací ``useTranslation`` prázdný objekt bez ``getResource`` —
    // snímek pak prostě nemá termíny, appka spadne na svůj fallback.
    const path = TERM_ROOT.slice(0, -1);
    const raw =
      typeof i18n?.getResource === "function"
        ? (i18n.getResource(i18n.language, VOCABULARY_NS, path) ??
          i18n.getResource(DEFAULT_TERMS_LANG, VOCABULARY_NS, path))
        : undefined;
    const terms: Record<string, string> = {};
    if (raw && typeof raw === "object") {
      for (const key of Object.keys(raw as Record<string, unknown>)) {
        const text = term(key, "");
        if (text) terms[key] = text;
      }
    }
    return { mode, terms };
  }, [i18n, mode, term]);
}
